import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertTriangle, CheckCircle } from 'lucide-react';

interface SecurityAlertProps {
  type: 'error' | 'warning' | 'success' | 'info';
  title: string;
  message: string;
  showRequirements?: boolean;
  className?: string;
}

export function SecurityAlert({ 
  type, 
  title, 
  message, 
  showRequirements = false,
  className = "" 
}: SecurityAlertProps) {
  const getIcon = () => {
    switch (type) {
      case 'error':
        return <AlertTriangle className="h-4 w-4" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4" />;
      case 'success':
        return <CheckCircle className="h-4 w-4" />;
      case 'info':
        return <Shield className="h-4 w-4" />;
      default:
        return <Shield className="h-4 w-4" />;
    }
  };

  const getAlertVariant = () => {
    switch (type) {
      case 'error':
        return 'destructive';
      case 'warning':
        return 'default';
      case 'success':
        return 'default';
      case 'info':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Alert variant={getAlertVariant()} className={className}>
      {getIcon()}
      <AlertDescription className="space-y-2">
        <div className="font-medium">{title}</div>
        <div className="text-sm">{message}</div>
        
        {showRequirements && (
          <div className="mt-3 p-3 bg-gray-50 rounded-md">
            <div className="text-xs font-medium text-gray-700 mb-2">Requisitos de senha forte:</div>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Mínimo 8 caracteres</li>
              <li>• Pelo menos uma letra maiúscula (A-Z)</li>
              <li>• Pelo menos uma letra minúscula (a-z)</li>
              <li>• Pelo menos um número (0-9)</li>
              <li>• Pelo menos um caractere especial (!@#$%^&*)</li>
            </ul>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}

interface PasswordRequirementsProps {
  className?: string;
}

export function PasswordRequirements({ className = "" }: PasswordRequirementsProps) {
  return (
    <div className={`p-3 bg-blue-50 border border-blue-200 rounded-md ${className}`}>
      <div className="text-sm font-medium text-blue-800 mb-2">
        🔒 Requisitos de Senha Segura
      </div>
      <div className="text-xs text-blue-700 space-y-1">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          Mínimo 8 caracteres
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          Pelo menos uma letra maiúscula (A-Z)
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          Pelo menos uma letra minúscula (a-z)
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          Pelo menos um número (0-9)
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          Pelo menos um caractere especial (!@#$%^&*)
        </div>
      </div>
    </div>
  );
} 